import { Button, Collapse, Stack } from '@ethings/ui-toolkit';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StageConfirmDialogCard } from './stage-confirm-dialog-card';
import { useStageConfirmDialogContext } from './stage-confirm-dialog-context';
import { StageDetailsEditor } from './stage-details-editor';
import { ThingDetailsEditor } from './thing-details-editor';

interface Props {
  setIsNextButtonDisabled: Dispatch<SetStateAction<boolean>>;
}

export const StageConfirmDialogEditStep: FC<Props> = ({ setIsNextButtonDisabled }) => {
  const { t } = useTranslation(['admin']);
  const [showMore, setShowMore] = useState(false);
  const { state, stage, thing, dispatch } = useStageConfirmDialogContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [areStageDetailsValid, setAreStageDetailsValid] = useState(false);
  const [areThingDetailsValid, setAreThingDetailsValid] = useState(false);

  const requiredStakeholders = state.stakeholders.filter(
    (stakeholder) => stakeholder.isRequired && !stakeholder.initialAccountId,
  );

  const optionalStakeholders = state.config?.isSelfClaim
    ? []
    : state.stakeholders.filter(
      (stakeholder) => !stakeholder.isRequired || stakeholder.initialAccountId,
    );

  const hasOptionalStakeholders = optionalStakeholders.length > 0;

  const hasStageProperties = Boolean(stage.stageProperties && Object.keys(stage.stageProperties).length > 0);
  const hasMissingRequiredProperties = useMemo(() => {
    if (!stage?.enterConditions?.requiredProperties) return [];
    return stage.enterConditions.requiredProperties.filter((prop) => {
      if (!thing) return true;

      const inProperties = thing.properties && prop in thing.properties;
      const inSharedProperties = thing.sharedProperties && prop in thing.sharedProperties;
      const isPropertyPresent = inProperties || inSharedProperties;

      return !isPropertyPresent;
    });
  }, [stage?.enterConditions?.requiredProperties, thing]);

  const hasRequiredProperties = Boolean(hasMissingRequiredProperties.length > 0);

  const useTwoColumnLayout = (hasStageProperties || hasRequiredProperties) && !isMobile;

  const onValidationChange = useCallback((role: string, isValid: boolean) => {
    dispatch({ type: 'SET_STAKEHOLDER_USER_VALIDITY', role, isValid });
  }, [dispatch]);

  const stakeholdersContent = (
    <Stack spacing={2}>
      {requiredStakeholders.map((stakeholder) => (
        <StageConfirmDialogCard
          key={stakeholder.role}
          stakeholderState={stakeholder}
          onValidationChange={onValidationChange}
        />
      ))}
      {!state.config?.isSelfClaim && hasOptionalStakeholders && (
        <>
          {requiredStakeholders.length > 0 && (
            <Button
              data-testid="stage-dialog-show-more-button"
              color="primary"
              onClick={() => setShowMore((val) => !val)}
              size="small"
            >
              {t(
                showMore
                  ? 'admin:page.thing-detail-update-stage-dialog.show-less.labels'
                  : 'admin:page.thing-detail-update-stage-dialog.show-more.labels',
              )}
            </Button>
          )}
          <Collapse in={showMore || requiredStakeholders.length === 0}>
            <Stack spacing={2}>
              {optionalStakeholders.map((stakeholder) => (
                <StageConfirmDialogCard
                  key={stakeholder.role}
                  stakeholderState={stakeholder}
                  onValidationChange={onValidationChange}
                />
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Stack>
  );

  const propertiesContent = useMemo(() => (
    <Stack spacing={3}>
      {hasStageProperties && <StageDetailsEditor onValidationChange={setAreStageDetailsValid} />}
      {hasMissingRequiredProperties.length > 0 && <ThingDetailsEditor onValidationChange={setAreThingDetailsValid} />}
    </Stack>
  ), [hasStageProperties, hasMissingRequiredProperties.length]);

  const stageDetailsValid = useMemo(() => (
    state.stageProperties && Object.keys(state.stageProperties).every((scope) => {
      const scopeDef = stage.stageProperties?.[scope];
      const requiredProps = scopeDef?.requiredProperties || [];
      return requiredProps.every((prop) =>
        state.stageProperties?.[scope]?.[prop]?.value !== undefined,
      );
    })
  ), [state.stageProperties, stage.stageProperties]);

  const thingDetailsValid = useMemo(() => (
    hasMissingRequiredProperties.every((propertyKey) => {
      const propertyValue = state.requiredProperties?.[propertyKey];
      const value = propertyValue && typeof propertyValue === 'object' && 'value' in propertyValue
        ? propertyValue.value
        : propertyValue;

      return value !== undefined && value !== null && value !== '';
    })
  ), [hasMissingRequiredProperties, state.requiredProperties]);

  useEffect(() => {
    setAreStageDetailsValid(!hasStageProperties || !!stageDetailsValid);
    setAreThingDetailsValid(!hasRequiredProperties || !!thingDetailsValid);
  }, [hasStageProperties, hasRequiredProperties, stageDetailsValid, thingDetailsValid]);

  useEffect(() => {
    setIsNextButtonDisabled(!(areStageDetailsValid && areThingDetailsValid));
  }, [areStageDetailsValid, areThingDetailsValid, setIsNextButtonDisabled]);

  if (useTwoColumnLayout) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {stakeholdersContent}
        </Grid>
        <Grid item xs={12} md={6}>
          {propertiesContent}
        </Grid>
      </Grid>
    );
  }

  return (
    <Stack spacing={3}>
      {stakeholdersContent}
      {propertiesContent}
    </Stack>
  );
};
